import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, } from 'react';
import debounce from 'lodash/fp/debounce';
import classNames from 'classnames';
import SmoothScrollbars from '../smoothScrollbars/SmoothScrollbars';
const RESIZE_THROTTLING = 200;
const DEFAULT_BOTTOM_BAR_HEIGHT = 54;
const ApplicationLayoutBody = forwardRef((props, ref) => {
    const { className, innerClassName, forceScrollbar = true, enableScrollToTop = true, banner, navigation, bottomBar, bottomBarHeight = DEFAULT_BOTTOM_BAR_HEIGHT, scrollResetTrigger, children } = props, remainingProps = __rest(props, ["className", "innerClassName", "forceScrollbar", "enableScrollToTop", "banner", "navigation", "bottomBar", "bottomBarHeight", "scrollResetTrigger", "children"]);
    const [offset, setOffset] = useState(0);
    const moduleContentRef = useRef(null);
    const layoutBodyRef = useRef(null);
    useImperativeHandle(ref, () => layoutBodyRef, []);
    // Reset scroll position when children are different. This fixes the issue of
    // changing body content (like in another route) and still having the same scroll position
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (moduleContentRef.current) {
            moduleContentRef.current.scrollTop(0);
            setOffset(0);
        }
    }, [scrollResetTrigger]);
    useEffect(() => {
        var _a;
        if (((_a = layoutBodyRef.current) === null || _a === void 0 ? void 0 : _a.parentNode) instanceof HTMLElement) {
            layoutBodyRef.current.parentNode.style.setProperty('--ApplicationLayoutBodyBottomBarHeight', `${bottomBarHeight}px`);
        }
    }, [bottomBarHeight]);
    const handleScroll = debounce(RESIZE_THROTTLING)(() => {
        if (moduleContentRef.current) {
            setOffset(moduleContentRef.current.getScrollTop());
        }
    });
    const handleToTop = () => {
        var _a;
        if (!moduleContentRef.current) {
            return;
        }
        const scrollWrapper = moduleContentRef.current.container;
        const scrollingElement = scrollWrapper.firstElementChild;
        const currentScroll = (_a = scrollingElement === null || scrollingElement === void 0 ? void 0 : scrollingElement.scrollTop) !== null && _a !== void 0 ? _a : 0;
        if (currentScroll > 0) {
            window.requestAnimationFrame(handleToTop);
            scrollingElement === null || scrollingElement === void 0 ? void 0 : scrollingElement.scrollTo(0, currentScroll - currentScroll / 5);
            scrollWrapper.classList.add('is-scrolling-to-top');
            setOffset(currentScroll);
        }
        else {
            scrollWrapper.classList.remove('is-scrolling-to-top');
        }
    };
    const classes = classNames('ApplicationLayoutBody', bottomBar && 'has-footer', className);
    const innerClasses = classNames('module-content', innerClassName && innerClassName);
    const offsetThreshold = window.innerHeight * 0.1;
    const scrollToTopClasses = classNames('scroll-to-top', offset > offsetThreshold && 'in');
    return (_jsxs(React.Fragment, { children: [_jsxs("div", Object.assign({}, remainingProps, { ref: layoutBodyRef, className: classes }, { children: [_jsxs("div", Object.assign({ className: 'module-content-wrapper' }, { children: [navigation && navigation, banner && banner, _jsx(SmoothScrollbars, Object.assign({ ref: moduleContentRef, slideIn: !forceScrollbar, largeTrack: true, trackOffset: true, className: innerClasses, onScroll: handleScroll }, { children: _jsx("div", Object.assign({ className: 'scrollbar-content' }, { children: children })) }))] })), enableScrollToTop && (_jsx("span", Object.assign({ className: scrollToTopClasses }, { children: _jsx("button", Object.assign({ type: 'button', className: 'btn btn-primary btn-icon-only', onClick: handleToTop }, { children: _jsx("span", { className: 'rioglyph rioglyph-arrow-up' }) })) })))] })), bottomBar && bottomBar] }));
});
export default ApplicationLayoutBody;
